function readText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

export function isLikelyOpaqueExecutiveIdentifier(value: unknown): boolean {
  const raw = readText(value);
  if (!raw) return false;
  if (raw.includes(' ') || raw.includes('@')) return false;
  return /^[A-Za-z0-9:_-]{4,}$/.test(raw);
}

export function formatExecutiveOwnerLabel(
  value: unknown,
  labelById?: ReadonlyMap<string, string> | null,
  fallback = 'Named owner unresolved',
): string {
  const raw = readText(value);
  if (!raw) return 'Unassigned';
  const mapped = labelById?.get(raw);
  if (mapped) {
    return isLikelyOpaqueExecutiveIdentifier(mapped) ? fallback : mapped;
  }
  return isLikelyOpaqueExecutiveIdentifier(raw) ? fallback : raw;
}

export function formatExecutiveAccountabilityLabel(value: unknown): string {
  const raw = readText(value);
  if (!raw || raw === 'Unassigned') return 'Unassigned';
  if (/^org node\b/i.test(raw) || /\bpos:[A-Z0-9_:-]+\b/i.test(raw)) {
    return 'Governed org node';
  }
  return isLikelyOpaqueExecutiveIdentifier(raw) ? 'Accountable executive unresolved' : raw;
}

export function formatExecutiveStrategyScopeLabel(
  value: unknown,
  fallback = 'Current strategy',
): string {
  const raw = readText(value);
  if (!raw) return fallback;
  if (isLikelyOpaqueExecutiveIdentifier(raw)) return fallback;
  const normalized = raw.replace(/\s+(portfolio scope|governed scope|org scenario)$/i, '').trim();
  return normalized || fallback;
}

export function sanitizeExecutiveText(
  value: unknown,
  options?: {
    scopeId?: string | null;
    scopeLabel?: string | null;
    strategyId?: string | null;
    strategyLabel?: string | null;
    ownerUserIds?: Array<string | null | undefined>;
  },
): string | null {
  const raw = readText(value);
  if (!raw) return null;

  let sanitized = raw;
  const scopeId = readText(options?.scopeId);
  const scopeLabel = readText(options?.scopeLabel) ?? 'governed scope';
  const strategyId = readText(options?.strategyId);
  const strategyLabel = readText(options?.strategyLabel) ?? 'current strategy';

  if (scopeId) {
    sanitized = sanitized.split(`Org node ${scopeId}`).join('Governed org node');
    sanitized = sanitized.split(scopeId).join(scopeLabel);
  }
  if (strategyId) {
    sanitized = sanitized.split(strategyId).join(strategyLabel);
  }
  for (const ownerUserId of options?.ownerUserIds ?? []) {
    const ownerId = readText(ownerUserId);
    if (!ownerId) continue;
    sanitized = sanitized.split(ownerId).join('unresolved decision owner');
  }

  sanitized = sanitized.replace(/\bOrg node\s+pos:[A-Z0-9_:-]+\b/gi, 'Governed org node');
  sanitized = sanitized
    .replace(/\bconfigured owner\b/gi, 'unresolved decision owner')
    .replace(/\bNo decision owner is configured\b/gi, 'No decision owner is assigned')
    .replace(/\bNo approvers are configured\b/gi, 'No approvers are assigned')
    .replace(/\bapprover(s?) configured\b/gi, 'approver$1 recorded')
    .replace(/\bis configured\b/gi, 'is assigned')
    .replace(/\bare configured\b/gi, 'are assigned')
    .replace(/\bconfigured\b/gi, 'recorded');

  return sanitized;
}
